/**
 * User Engagement Analytics Page
 *
 * Firebase Auth-based metrics including DAU/WAU/MAU,
 * stickiness ratio, auth methods, retention cohorts, and login health.
 */

import { useState, useEffect } from 'react';
import { thirdPartyAnalyticsAPI } from '../services/api';
import { useEnvironment } from '../context/EnvironmentContext';
import PageHeader from '../components/analytics/PageHeader';
import PeriodSelector from '../components/analytics/PeriodSelector';
import MetricCard from '../components/analytics/MetricCard';
import TrendChart from '../components/founder-dashboard/TrendChart';
import DataTable from '../components/founder-dashboard/DataTable';
import DistributionChart from '../components/founder-dashboard/DistributionChart';
import {
  UsersIcon,
  CalendarDaysIcon,
  CalendarIcon,
  ChartBarIcon,
  UserPlusIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

export default function UserEngagementPage() {
  const { currentEnv } = useEnvironment();
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [authMethods, setAuthMethods] = useState(null);
  const [retention, setRetention] = useState(null);
  const [loginHealth, setLoginHealth] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewRes, trendsRes, authRes, retentionRes, healthRes] = await Promise.all([
        thirdPartyAnalyticsAPI.getUserEngagementOverview(),
        thirdPartyAnalyticsAPI.getUserEngagementTrends(period),
        thirdPartyAnalyticsAPI.getUserEngagementAuthMethods(),
        thirdPartyAnalyticsAPI.getUserEngagementRetention(),
        thirdPartyAnalyticsAPI.getUserEngagementLoginHealth(),
      ]);
      setOverview(overviewRes.data);
      setTrends(trendsRes.data);
      setAuthMethods(authRes.data);
      setRetention(retentionRes.data);
      setLoginHealth(healthRes.data);
    } catch (err) {
      console.error('[UserEngagementPage] Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentEnv, period]);

  // Calculate stickiness status
  const getStickinessStatus = () => {
    const ratio = overview?.wauMauRatio || 0;
    if (ratio < 0.4) return 'critical';
    if (ratio < 0.6) return 'warning';
    return 'healthy';
  };

  const getLoginHealthStatus = () => {
    const rate = overview?.loginSuccessRate24h || 100;
    if (rate < 95) return 'critical';
    if (rate < 98) return 'warning';
    return 'healthy';
  };

  if (error) {
    return (
      <div className="p-6">
        <PageHeader title="User Engagement" />
        <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium">Error loading user engagement data</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Prepare trend chart data with WAU/MAU ratio (stickiness)
  // MAU is calculated as rolling 30-day unique users, approximated here using the overview MAU
  const mau = overview?.mau || 1; // Use current MAU from overview
  const rawTrendData = trends?.data || [];
  const trendData = rawTrendData.map(item => ({
    ...item,
    // Calculate WAU/MAU ratio (stickiness) - WAU divided by MAU
    // Values typically range 0-1, where 1.0 means all monthly users are active weekly
    wauMauRatio: mau > 0 ? Math.min(item.wau / mau, 1) : 0,
  }));
  const trendLines = [
    { key: 'dau', name: 'DAU', color: '#3B82F6' },
    { key: 'wau', name: 'WAU', color: '#22C55E' },
    { key: 'wauMauRatio', name: 'WAU/MAU', color: '#8B5CF6', yAxisId: 'right' },
  ];

  // Auth methods distribution
  const authDistribution = {};
  (authMethods?.authMethods || []).forEach((m) => {
    authDistribution[m.authProvider] = m.percentage / 100;
  });

  // Retention cohort table columns (monthly cohort retention)
  const retentionColumns = [
    {
      key: 'cohortMonth',
      header: 'Cohort',
      sortable: true,
      render: (val) => val ? new Date(val).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '-',
    },
    { key: 'cohortSize', header: 'Users', sortable: true },
    {
      key: 'month0',
      header: 'Month 0',
      sortable: true,
      render: (val) => `${(val || 0).toFixed(1)}%`,
    },
    {
      key: 'month1',
      header: 'Month 1',
      sortable: true,
      render: (val) => `${(val || 0).toFixed(1)}%`,
    },
    {
      key: 'month2',
      header: 'Month 2',
      sortable: true,
      render: (val) => `${(val || 0).toFixed(1)}%`,
    },
    {
      key: 'month3',
      header: 'Month 3',
      sortable: true,
      render: (val) => `${(val || 0).toFixed(1)}%`,
    },
  ];

  // Stickiness ratio as a percentage for progress bar
  const stickinessRatio = overview?.wauMauRatio || 0;
  const stickinessPercent = Math.min(stickinessRatio * 100, 100);

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <PageHeader
        title="User Engagement"
        description="Active users and engagement metrics"
        onRefresh={fetchData}
        loading={loading}
      >
        <PeriodSelector value={period} onChange={setPeriod} />
      </PageHeader>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Daily Active Users"
          value={overview?.dau}
          icon={UsersIcon}
          change={overview?.dauChange}
          loading={loading}
        />
        <MetricCard
          title="Weekly Active Users"
          value={overview?.wau}
          icon={CalendarDaysIcon}
          change={overview?.wauChange}
          loading={loading}
        />
        <MetricCard
          title="Monthly Active Users"
          value={overview?.mau}
          icon={CalendarIcon}
          change={overview?.mauChange}
          loading={loading}
        />
        <MetricCard
          title="WAU/MAU Ratio"
          value={stickinessRatio}
          format="decimal"
          icon={ChartBarIcon}
          status={getStickinessStatus()}
          loading={loading}
        />
      </div>

      {/* Stickiness Progress Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Stickiness Score
          </h3>
          <span className={`text-sm font-medium ${
            getStickinessStatus() === 'critical' ? 'text-red-600' :
            getStickinessStatus() === 'warning' ? 'text-yellow-600' :
            'text-green-600'
          }`}>
            WAU/MAU = {stickinessRatio.toFixed(2)} (target: 0.80+)
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
          <div
            className={`h-4 rounded-full transition-all duration-500 ${
              getStickinessStatus() === 'critical' ? 'bg-red-500' :
              getStickinessStatus() === 'warning' ? 'bg-yellow-500' :
              'bg-green-500'
            }`}
            style={{ width: `${stickinessPercent}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-400">
          <span>0</span>
          <span className="text-yellow-500">0.60</span>
          <span className="text-green-500">0.80</span>
          <span>1.0</span>
        </div>
      </div>

      {/* Active Users Trend */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Active Users Trend ({period})
        </h3>
        {loading ? (
          <div className="h-64 bg-gray-50 rounded-lg animate-pulse"></div>
        ) : (
          <TrendChart
            data={trendData}
            lines={trendLines}
            height={280}
            xAxisKey="date"
            type="line"
            rightAxisLabel="Ratio"
          />
        )}
      </div>

      {/* New Signups + Auth Methods */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* New Signups */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            New Signups
          </h3>
          {loading ? (
            <div className="h-48 bg-gray-50 rounded-lg animate-pulse"></div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <UserPlusIcon className="w-5 h-5 text-blue-600" />
                  <span className="text-sm text-gray-600">Today</span>
                </div>
                <span className="text-2xl font-bold text-blue-700">
                  {overview?.signupsToday || 0}
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <CalendarDaysIcon className="w-5 h-5 text-gray-600" />
                  <span className="text-sm text-gray-600">This Week</span>
                </div>
                <span className="text-2xl font-bold text-gray-700">
                  {overview?.signupsThisWeek || 0}
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <CalendarIcon className="w-5 h-5 text-gray-600" />
                  <span className="text-sm text-gray-600">This Month</span>
                </div>
                <span className="text-2xl font-bold text-gray-700">
                  {overview?.signupsThisMonth || 0}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Auth Methods */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Authentication Methods
          </h3>
          {loading ? (
            <div className="h-48 bg-gray-50 rounded-lg animate-pulse"></div>
          ) : (
            <DistributionChart
              data={authDistribution}
              height={200}
              showPercentage
            />
          )}
        </div>
      </div>

      {/* Retention Cohorts */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Retention Cohorts
        </h3>
        <DataTable
          data={retention?.cohorts || []}
          columns={retentionColumns}
          loading={loading}
          emptyMessage="No retention data available"
        />
      </div>

      {/* Login Health */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Login Health
        </h3>
        {loading ? (
          <div className="h-32 bg-gray-50 rounded-lg animate-pulse"></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className={`flex items-center gap-3 p-4 rounded-lg ${
              getLoginHealthStatus() === 'critical' ? 'bg-red-50 border border-red-100' :
              getLoginHealthStatus() === 'warning' ? 'bg-yellow-50 border border-yellow-100' :
              'bg-green-50 border border-green-100'
            }`}>
              {getLoginHealthStatus() === 'healthy' ? (
                <CheckCircleIcon className="w-8 h-8 text-green-600" />
              ) : (
                <ExclamationCircleIcon className={`w-8 h-8 ${
                  getLoginHealthStatus() === 'critical' ? 'text-red-600' : 'text-yellow-600'
                }`} />
              )}
              <div>
                <p className="text-sm text-gray-500">Success Rate (24h)</p>
                <p className={`text-xl font-bold ${
                  getLoginHealthStatus() === 'critical' ? 'text-red-700' :
                  getLoginHealthStatus() === 'warning' ? 'text-yellow-700' :
                  'text-green-700'
                }`}>
                  {(overview?.loginSuccessRate24h || 100).toFixed(2)}%
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <ShieldCheckIcon className="w-8 h-8 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Success Rate (7d)</p>
                <p className="text-xl font-bold text-gray-700">
                  {(overview?.loginSuccessRate7d || 100).toFixed(2)}%
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <ExclamationCircleIcon className="w-8 h-8 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Failed Logins (24h)</p>
                <p className="text-xl font-bold text-gray-700">
                  {overview?.loginFailure24h || 0}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
